import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useIntercom } from 'react-use-intercom';
import { useRouter } from 'next/router';
import { cn, HeadphoneIcon } from '@restworld/utils-common';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';
import { useGettingStartedPage } from './restaurant-onboarding-modal.hooks';

interface IRestaurantOrContactAlreadyExistsProps {
  title?: string;
  subtitle?: string;
  /** Optional custom handler for the "Go to Login" button click.
   *  When provided, overrides the default `router.push('/login')` behavior.
   *  Used by org onboarding to redirect with a return URL query param. */
  onLoginRedirect?: () => void;
}

const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full h-full inline-flex flex-col justify-center items-center gap-10', className)} {...props} />
);
const Section = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch flex flex-col justify-start items-start gap-4', className)} {...props} />
);
const Title = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5
    className={cn('self-stretch text-center justify-start text-neutral-700 text-xl font-semibold', className)}
    {...props}
  />
);
const Subtitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5
    className={cn('self-stretch text-center justify-start text-neutral-200 text-base font-medium', className)}
    {...props}
  />
);
const HeaderTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('text-center justify-start text-neutral-700 text-sm font-medium', className)} {...props} />
);
const ButtonTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('text-purple-700 text-base font-semibold', className)} {...props} />
);

const RestaurantOrContactAlreadyExists: React.FunctionComponent<IRestaurantOrContactAlreadyExistsProps> = ({
  title,
  subtitle,
  onLoginRedirect,
}) => {
  const { t } = useTranslation('restaurant-onboarding');
  const { showMessages } = useIntercom();
  const router = useRouter();
  const isGettingStartedPage = useGettingStartedPage();

  return (
    <Container>
      <Section>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </Section>
      {isGettingStartedPage && (
        <Section>
          <HeaderTitle>{t('was_it_you_or_someone_from_your_staff')}</HeaderTitle>
          <ButtonV2
            EndIcon={ArrowRightIcon}
            variant="secondary-restworld"
            className="w-full"
            size="md"
            onClick={(e) => {
              e.preventDefault();
              if (onLoginRedirect) {
                onLoginRedirect();
              } else {
                router.push('/login');
              }
            }}
          >
            <ButtonTitle>{t('go_to_login')}</ButtonTitle>
          </ButtonV2>
        </Section>
      )}
      <Section>
        <HeaderTitle>{t('it_wasnt_you')}</HeaderTitle>
        <ButtonV2
          EndIcon={HeadphoneIcon}
          variant="secondary-restworld"
          className="w-full"
          size="md"
          onClick={(e) => {
            e.preventDefault();
            showMessages();
          }}
        >
          <ButtonTitle>{t('ask_support')}</ButtonTitle>
        </ButtonV2>
      </Section>
    </Container>
  );
};

export default RestaurantOrContactAlreadyExists;
